/**
 * The fold-able section header used inside an Applicants-tab card
 * ("Prior loans & servicing", "Credited experience"): a disclosure arrow on
 * the LEFT (down when open, right when folded), a 15/600 title, and — when
 * folded — a small summary pill so the collapsed section still says what it
 * holds. The caller owns `open`; anything on the right (refresh, "Checked …")
 * is the caller's to lay out beside this — the header itself fills the rest
 * of the row, so the whole row toggles.
 *
 * Clicks stop propagation: the card underneath opens the borrower sidebar.
 */
import { ExpandMoreRounded } from "@mui/icons-material";
import { Box, ButtonBase } from "@mui/material";
import PropTypes from "prop-types";

import Txt from "../../../components/typography/Txt";
import { ink, slate } from "../../../theme/colors";

export const SummaryPill = ({ bg, border, color, dot, children }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      minWidth: 0,
      ml: 0.75,
      px: "8px",
      py: "2px",
      borderRadius: "999px",
      border: `1px solid ${border || slate[200]}`,
      background: bg || slate[50],
      color: color || slate[600],
      fontSize: 12,
      fontWeight: 500,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }}
  >
    {dot && (
      <Box
        component="span"
        sx={{
          width: 6,
          height: 6,
          flexShrink: 0,
          borderRadius: "50%",
          background: dot,
        }}
      />
    )}
    {children}
  </Box>
);

SummaryPill.propTypes = {
  bg: PropTypes.string,
  border: PropTypes.string,
  color: PropTypes.string,
  dot: PropTypes.string,
  children: PropTypes.node,
};

const CollapsibleSectionHeader = ({ title, open, onToggle, summary, meta }) => (
  <ButtonBase
    onClick={(e) => {
      e.stopPropagation();
      onToggle();
    }}
    onMouseDown={(e) => e.stopPropagation()}
    aria-expanded={open}
    sx={{
      // Fills the row: anywhere on it toggles. Controls that must not toggle
      // (refresh) sit OUTSIDE this button, beside it in the caller's row.
      flex: 1,
      gap: 0.5,
      minWidth: 0,
      py: "4px",
      pr: 1,
      ml: "-4px",
      pl: "4px",
      borderRadius: "8px",
      justifyContent: "flex-start",
      "&:hover": { background: slate[50] },
    }}
  >
    <ExpandMoreRounded
      sx={{
        fontSize: 22,
        flexShrink: 0,
        color: slate[500],
        transform: open ? "none" : "rotate(-90deg)",
        transition: "transform 0.15s",
      }}
    />
    <Txt
      sx={{
        fontSize: 15,
        fontWeight: 600,
        color: ink,
        letterSpacing: "-0.01em",
        whiteSpace: "nowrap",
      }}
    >
      {title}
    </Txt>
    {!open && summary}
    {meta}
  </ButtonBase>
);

CollapsibleSectionHeader.propTypes = {
  title: PropTypes.node.isRequired,
  open: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  /** Shown only while folded — usually a SummaryPill. */
  summary: PropTypes.node,
  /** Shown open or folded, after the summary — counts, flags. */
  meta: PropTypes.node,
};

export default CollapsibleSectionHeader;
